const STORAGE_KEY = "matrix_auth";

export interface MatrixAuth {
  accessToken: string;
  userId: string;
  homeserver: string;
  deviceId: string;
}

export function getStoredAuth(): MatrixAuth | null {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (!stored) return null;
  try {
    return JSON.parse(stored);
  } catch {
    return null;
  }
}

export function storeAuth(auth: MatrixAuth): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(auth));
}

export function clearAuth(): void {
  localStorage.removeItem(STORAGE_KEY);
}

export async function matrixLogin(
  homeserver: string,
  user: string,
  password: string
): Promise<MatrixAuth> {
  const baseUrl = homeserver.replace(/\/+$/, "");
  const res = await fetch(`${baseUrl}/_matrix/client/v3/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      type: "m.login.password",
      identifier: { type: "m.id.user", user },
      password,
    }),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: "Login failed" }));
    throw new Error(err.error || `Login failed (${res.status})`);
  }

  const data = await res.json();
  const auth: MatrixAuth = {
    accessToken: data.access_token,
    userId: data.user_id,
    homeserver: baseUrl,
    deviceId: data.device_id,
  };
  storeAuth(auth);
  return auth;
}

export async function getJoinedRooms(
  auth: MatrixAuth
): Promise<string[]> {
  const res = await fetch(
    `${auth.homeserver}/_matrix/client/v3/joined_rooms`,
    { headers: { Authorization: `Bearer ${auth.accessToken}` } }
  );
  if (!res.ok) throw new Error("Failed to fetch rooms");
  const data = await res.json();
  return data.joined_rooms;
}

export async function sendMessage(
  auth: MatrixAuth,
  roomId: string,
  body: string,
  formatted?: string
): Promise<string> {
  const txnId = `m${Date.now()}.${Math.random().toString(36).slice(2)}`;
  const encodedRoom = encodeURIComponent(roomId);
  const content: Record<string, string> = {
    msgtype: "m.text",
    body,
  };
  if (formatted) {
    content.format = "org.matrix.custom.html";
    content.formatted_body = formatted;
  }

  const res = await fetch(
    `${auth.homeserver}/_matrix/client/v3/rooms/${encodedRoom}/send/m.room.message/${txnId}`,
    {
      method: "PUT",
      headers: {
        Authorization: `Bearer ${auth.accessToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(content),
    }
  );

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: "Send failed" }));
    throw new Error(err.error || `Send failed (${res.status})`);
  }

  const data = await res.json();
  return data.event_id;
}

export async function uploadFile(
  auth: MatrixAuth,
  data: Blob,
  filename: string,
  contentType: string
): Promise<string> {
  // Try authenticated media endpoint first (Synapse 1.98+), fall back to legacy
  const endpoints = [
    `${auth.homeserver}/_matrix/client/v1/media/upload?filename=${encodeURIComponent(filename)}`,
    `${auth.homeserver}/_matrix/media/v3/upload?filename=${encodeURIComponent(filename)}`,
  ];

  for (const url of endpoints) {
    const res = await fetch(url, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${auth.accessToken}`,
        "Content-Type": contentType,
      },
      body: data,
    });

    if (res.status === 404 || res.status === 400) continue;

    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: "Upload failed" }));
      throw new Error(err.error || `Upload failed (${res.status})`);
    }

    const result = await res.json();
    console.log("[Matrix] Upload response:", result);
    return result.content_uri;
  }

  throw new Error("Media upload not supported on this server");
}

export async function sendFileMessage(
  auth: MatrixAuth,
  roomId: string,
  mxcUri: string,
  filename: string,
  size: number,
  mimetype: string
): Promise<string> {
  const txnId = `m${Date.now()}.${Math.random().toString(36).slice(2)}`;
  const encodedRoom = encodeURIComponent(roomId);

  const res = await fetch(
    `${auth.homeserver}/_matrix/client/v3/rooms/${encodedRoom}/send/m.room.message/${txnId}`,
    {
      method: "PUT",
      headers: {
        Authorization: `Bearer ${auth.accessToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        msgtype: "m.file",
        body: filename,
        filename,
        url: mxcUri,
        info: {
          mimetype,
          size,
        },
      }),
    }
  );

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: "Send failed" }));
    throw new Error(err.error || `Send failed (${res.status})`);
  }

  const data = await res.json();
  return data.event_id;
}

export async function getRoomName(
  auth: MatrixAuth,
  roomId: string
): Promise<string> {
  const encodedRoom = encodeURIComponent(roomId);
  const res = await fetch(
    `${auth.homeserver}/_matrix/client/v3/rooms/${encodedRoom}/state/m.room.name`,
    { headers: { Authorization: `Bearer ${auth.accessToken}` } }
  );
  if (!res.ok) return roomId;
  const data = await res.json();
  return data.name || roomId;
}

export async function isSpaceRoom(
  auth: MatrixAuth,
  roomId: string
): Promise<boolean> {
  const encodedRoom = encodeURIComponent(roomId);
  const res = await fetch(
    `${auth.homeserver}/_matrix/client/v3/rooms/${encodedRoom}/state/m.room.create`,
    { headers: { Authorization: `Bearer ${auth.accessToken}` } }
  );
  if (!res.ok) return false;
  const data = await res.json();
  return data.type === "m.space";
}

export interface RoomInfo {
  id: string;
  name: string;
}

export async function getPublishableRooms(
  auth: MatrixAuth
): Promise<RoomInfo[]> {
  const roomIds = await getJoinedRooms(auth);
  const rooms: RoomInfo[] = [];
  for (const id of roomIds) {
    const space = await isSpaceRoom(auth, id);
    if (space) continue;
    const name = await getRoomName(auth, id);
    // Filter out rooms with no name (DMs, system rooms, unnamed spaces)
    if (name === id) continue;
    rooms.push({ id, name });
  }
  // Sort rooms with "sop" in name first
  rooms.sort((a, b) => {
    const aHasSop = a.name.toLowerCase().includes("sop") ? 0 : 1;
    const bHasSop = b.name.toLowerCase().includes("sop") ? 0 : 1;
    return aHasSop - bHasSop || a.name.localeCompare(b.name);
  });
  return rooms;
}

export async function createRoom(
  auth: MatrixAuth,
  name: string,
  topic?: string
): Promise<string> {
  const res = await fetch(
    `${auth.homeserver}/_matrix/client/v3/createRoom`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${auth.accessToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        name,
        topic: topic || "",
        visibility: "private",
        preset: "private_chat",
      }),
    }
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: "Create room failed" }));
    throw new Error(err.error || `Create room failed (${res.status})`);
  }
  const data = await res.json();
  return data.room_id;
}

export async function addRoomToSpace(
  auth: MatrixAuth,
  spaceId: string,
  childRoomId: string
): Promise<void> {
  const encodedSpace = encodeURIComponent(spaceId);
  const encodedChild = encodeURIComponent(childRoomId);
  const res = await fetch(
    `${auth.homeserver}/_matrix/client/v3/rooms/${encodedSpace}/state/m.space.child/${encodedChild}`,
    {
      method: "PUT",
      headers: {
        Authorization: `Bearer ${auth.accessToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ via: [new URL(auth.homeserver).hostname] }),
    }
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: "Add to space failed" }));
    throw new Error(err.error || `Add to space failed (${res.status})`);
  }
}

export async function getSpaceRooms(
  auth: MatrixAuth
): Promise<RoomInfo[]> {
  const roomIds = await getJoinedRooms(auth);
  const spaces: RoomInfo[] = [];
  for (const id of roomIds) {
    const space = await isSpaceRoom(auth, id);
    if (!space) continue;
    const name = await getRoomName(auth, id);
    spaces.push({ id, name });
  }
  return spaces;
}

// ────────────────────────────────────────────────────────────────────
// Personal VoxDoc room — single, locked publish target per user
// ────────────────────────────────────────────────────────────────────

export async function getDisplayName(auth: MatrixAuth): Promise<string> {
  const userId = encodeURIComponent(auth.userId);
  const res = await fetch(
    `${auth.homeserver}/_matrix/client/v3/profile/${userId}/displayname`,
    { headers: { Authorization: `Bearer ${auth.accessToken}` } }
  );
  if (res.ok) {
    const data = await res.json();
    if (data.displayname) return data.displayname;
  }
  // Fallback: derive from userId localpart "todd.vanbelkum" → "Todd Vanbelkum"
  const local = auth.userId.split(":")[0].slice(1);
  return local
    .split(".")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

export async function joinRoom(auth: MatrixAuth, roomId: string): Promise<void> {
  const res = await fetch(
    `${auth.homeserver}/_matrix/client/v3/rooms/${encodeURIComponent(roomId)}/join`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${auth.accessToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({}),
    }
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: "Join failed" }));
    throw new Error(err.error || `Join failed (${res.status})`);
  }
}

interface SyncRoomEntry {
  state?: { events?: Array<{ type: string; content?: { name?: string } }> };
  invite_state?: { events?: Array<{ type: string; content?: { name?: string } }> };
}

interface SyncResponse {
  rooms?: {
    join?: Record<string, SyncRoomEntry>;
    invite?: Record<string, SyncRoomEntry>;
  };
}

/**
 * Normalize a room name for VoxDoc matching — tolerates the same em-dash /
 * lowercase / spacing variants the backfill script normalized.
 */
function normalizeVoxDocName(name: string): string {
  return name.toLowerCase().replace(/[\s–—-]+/g, " ").trim();
}

/**
 * Find the user's personal VoxDoc room — handles joined AND invited.
 * After backfill, most users have a pending invite to their VoxDoc room
 * (the script invited them; auto-join only happens when user opens Matrix).
 * This function will accept the invite if needed.
 *
 * Lazy-creates as a final fallback so users provisioned outside the
 * backfill flow still get a working publish target.
 */
export async function findOrCreateVoxDocRoom(auth: MatrixAuth): Promise<RoomInfo> {
  const displayName = await getDisplayName(auth);
  const canonical = `VoxDoc - ${displayName}`;
  const targetNorm = normalizeVoxDocName(canonical);

  // Single sync to get joined + invited rooms with their state-event names.
  const filter = encodeURIComponent(
    JSON.stringify({
      room: {
        state: { types: ["m.room.name"], lazy_load_members: true },
        timeline: { limit: 0 },
        ephemeral: { limit: 0 },
        account_data: { limit: 0 },
      },
      presence: { limit: 0 },
      account_data: { limit: 0 },
    }),
  );
  const syncRes = await fetch(
    `${auth.homeserver}/_matrix/client/v3/sync?timeout=0&filter=${filter}`,
    { headers: { Authorization: `Bearer ${auth.accessToken}` } },
  );
  if (!syncRes.ok) {
    throw new Error(`VoxDoc sync failed: ${syncRes.status}`);
  }
  const sync = (await syncRes.json()) as SyncResponse;

  const extractName = (entry: SyncRoomEntry): string | undefined => {
    const events = entry.state?.events ?? entry.invite_state?.events ?? [];
    const nameEvt = events.find((e) => e.type === "m.room.name");
    return nameEvt?.content?.name;
  };

  // Pass 1: already joined?
  for (const [rid, entry] of Object.entries(sync.rooms?.join ?? {})) {
    const nm = extractName(entry);
    if (nm && normalizeVoxDocName(nm) === targetNorm) {
      return { id: rid, name: nm };
    }
  }

  // Pass 2: invited (e.g., from backfill script) — accept the invite
  for (const [rid, entry] of Object.entries(sync.rooms?.invite ?? {})) {
    const nm = extractName(entry);
    if (nm && normalizeVoxDocName(nm) === targetNorm) {
      await joinRoom(auth, rid);
      return { id: rid, name: nm };
    }
  }

  // Pass 3: lazy create — defensive. Backfill should already have created it
  // for active staff; this covers off-roster / new-hire / edge-case users.
  console.warn(`[VoxDoc] No existing room for "${displayName}", creating ${canonical}`);
  const newId = await createRoom(auth, canonical, `Personal VoxDoc room for ${displayName}.`);
  return { id: newId, name: canonical };
}
